import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeBlock from '../common/CodeBlock';
import Citation from '../etch/Citation';
import MermaidDiagram from './MermaidDiagram.jsx';
import { CITATION_MARKER_RE } from './trust';

const CITATION_HREF = '#etch-citation-';

/** Fence languages that mermaid understands, as models label them in practice. */
const MERMAID_LANGUAGES = new Set(['mermaid', 'mermaidjs', 'mmd']);

/**
 * remark transform that turns the literal `[S1]` markers the model writes into
 * mdast `link` nodes with a private href, so react-markdown hands them to the
 * `a` component override below and we can render a real `<Citation>` button
 * inside the flowing prose (design spec §9.1: sources sit at the statement,
 * not in a footnote block).
 *
 * Text inside code spans/blocks is untouched — those mdast node types are not
 * `text`, so a `[S1]` inside a snippet stays literal.
 */
function remarkEtchCitations() {
  const split = (node) => {
    const parts = [];
    const re = new RegExp(CITATION_MARKER_RE.source, 'gi');
    let lastIndex = 0;
    let match;
    while ((match = re.exec(node.value)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', value: node.value.slice(lastIndex, match.index) });
      }
      const id = match[1].toLowerCase();
      parts.push({
        type: 'link',
        url: `${CITATION_HREF}${id}`,
        title: null,
        children: [{ type: 'text', value: match[0] }],
      });
      lastIndex = match.index + match[0].length;
    }
    if (parts.length === 0) return null;
    if (lastIndex < node.value.length) {
      parts.push({ type: 'text', value: node.value.slice(lastIndex) });
    }
    return parts;
  };

  const walk = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    const next = [];
    for (const child of node.children) {
      if (child?.type === 'text' && typeof child.value === 'string') {
        const parts = split(child);
        if (parts) {
          next.push(...parts);
          continue;
        }
      }
      walk(child);
      next.push(child);
    }
    node.children = next;
  };

  return (tree) => walk(tree);
}

const PLUGINS = [remarkGfm, remarkEtchCitations];

/**
 * Markdown body of a chat message, with inline citations.
 *
 * @param {object} props
 * @param {string} props.content
 * @param {Map<string, object>|object} [props.sourcesById] - resolved descriptors keyed by id.
 * @param {string} [props.activeSourceId]
 * @param {(sourceId: string) => void} [props.onOpenSource]
 * @param {boolean} [props.streaming] - answer still arriving; a half-written
 *   ```mermaid fence is not a diagram yet, so it stays a code block until the
 *   turn completes rather than re-parsing (and failing) on every chunk.
 */
export function MessageMarkdown({ content, sourcesById, activeSourceId, onOpenSource, streaming = false }) {
  const lookup = useMemo(() => {
    if (sourcesById instanceof Map) return sourcesById;
    const map = new Map();
    for (const [key, value] of Object.entries(sourcesById || {})) map.set(key, value);
    return map;
  }, [sourcesById]);

  const components = useMemo(
    () => ({
      code({ inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        if (!inline && (language || String(children).includes('\n'))) {
          const code = String(children).replace(/\n$/, '');
          if (!streaming && MERMAID_LANGUAGES.has(language.toLowerCase())) {
            return <MermaidDiagram chart={code} language={language} />;
          }
          return <CodeBlock code={code} language={language} />;
        }
        return (
          <code className="etch-code-inline" {...props}>
            {children}
          </code>
        );
      },
      pre({ children }) {
        return <>{children}</>;
      },
      a({ href, children, ...props }) {
        if (typeof href === 'string' && href.startsWith(CITATION_HREF)) {
          const id = href.slice(CITATION_HREF.length);
          return (
            <Citation
              sourceId={id}
              source={lookup.get(id)}
              active={activeSourceId === id}
              onOpen={onOpenSource}
            />
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
    }),
    [lookup, activeSourceId, onOpenSource, streaming]
  );

  return (
    <div className="etch-message__markdown etch-prose">
      <ReactMarkdown remarkPlugins={PLUGINS} components={components}>
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}

export default MessageMarkdown;
